import {Listing} from "@/models/Listing";
import {License} from "@/models/License";
import {Property} from "@/models/Property";
import chunk from "@/chunk";

/**
 * A Property instance that can include matching listings and license.
 */
export class PropertyDetails extends Property {

  static async one(city, propertyId) {
    const property = await super.one(city, propertyId);
    await this.resolveProperty(city, property);
    return property;
  }

  static async list(city) {
    const properties = await super.list(city);
    await this.resolveProperties(city, properties);
    return properties;
  }

  static async searchWithGoogle(city, address) {
    const properties = await city.normalizePropertiesWithGoogle(address);
    return await this.resolveProperties(city, properties);
  }

  static async findNearby(city, latlng, radius, limit) {
    const lat = latlng?.lat;
    const lng = latlng?.lng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return [];
    }
    const properties = await city.findNearbyProperties({ lat, lng, radius, limit });
    return await this.resolveProperties(city, properties);
  }

  static async resolveProperty(city, property) {
    return (await this.resolveProperties(city, [property]))[0];
  }

  /**
   * Given a list of Property objects, create and populate a corresponding PropertyDetails for each.
   * @param {City} city
   * @param {Property[]} properties
   * @return {Promise<PropertyDetails[]>}
   */
  static async resolveProperties(city, properties) {
    const lookup = {};

    // Split up the property ID query into buckets with max size 30. This is the maximum Firestore allows for IN query
    const propertyIdBuckets = chunk(
      properties.map(property => {
        lookup[property.id] = new PropertyDetails(city, property.exportForCopy());
        return property.id;
      }), 30);

    // In parallel, fetch all listing and license buckets
    const [ listingResponseBuckets, licenseResponseBuckets ] = await Promise.all([
      Promise.all(propertyIdBuckets.map(async propertyIds => {
        const {results:listings} = await Listing.query(city, {
          constraints: [
            ['verifiedPropertyId', 'in', propertyIds],
            ['status', '==', 'active']
          ]
        });
        return listings;
      })),
      Promise.all(propertyIdBuckets.map(async propertyIds => {
        const {results:licenses} = await License.query(city, {
          constraints: [
            ['propertyId', 'in', propertyIds]
          ]
        });
        return licenses;
      }))
    ]);

    // For each listing in each bucket, add it to the corresponding property
    for (const bucket of listingResponseBuckets) {
      for (const listing of bucket) {
        lookup[listing.verifiedPropertyId].listings.push(listing);
      }
    }

    // For each license in each bucket, set it to the corresponding property
    for (const bucket of licenseResponseBuckets) {
      for (const license of bucket) {
        lookup[license.propertyId].license = license;
      }
    }

    return Object.values(lookup);
  }

  constructor(city, doc) {
    super(city, doc);
    this.listings = [];
    this.license = null;
  }

}
